/**
 * 运输公司端 WebSocket：连接 /infra/ws，接收审核菜单刷新提醒
 */
import { getToken, getRefreshToken } from '@/utils/auth'
import config from '@/config'

const MESSAGE_TYPE = 'dispatch-audit-remind'
const HEARTBEAT_INTERVAL_MS = 25000
const RECONNECT_BASE_MS = 2000
const RECONNECT_MAX_MS = 30000

/** @typedef {'idle'|'connecting'|'online'|'reconnecting'|'offline'} PushStatus */

/**
 * 握手必须用 accessToken（后端 checkAccessToken），否则连上了也无法按用户推送。
 * 无 accessToken 时再退回 refreshToken（可能仅能握手、收不到定向消息）。
 */
function resolveWsToken() {
  return getToken() || getRefreshToken() || ''
}

/**
 * 开发环境优先直连后端，避开 devServer WS 代理偶发失效；
 * 生产用页面同源 /infra/ws（由 Nginx 反代）。
 */
function resolveWsBase() {
  const explicit = process.env.VUE_APP_WS_BASE
  if (explicit) {
    return String(explicit).replace(/\/$/, '')
  }

  const proxyTarget = process.env.VUE_APP_PROXY_TARGET
  if (proxyTarget && /^https?:\/\//i.test(proxyTarget)) {
    return String(proxyTarget).replace(/^http/i, 'ws').replace(/\/$/, '')
  }

  const baseApi = config.baseApi || ''
  if (/^https?:\/\//i.test(baseApi)) {
    try {
      const u = new URL(baseApi)
      const wsProtocol = u.protocol === 'https:' ? 'wss:' : 'ws:'
      return `${wsProtocol}//${u.host}`
    } catch {
      /* fall through */
    }
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}`
}

function buildWsUrl() {
  const token = resolveWsToken()
  if (!token) {
    console.warn('[dispatchWs] 无 token，无法连接')
    return ''
  }
  const base = resolveWsBase()
  return `${base}/infra/ws?token=${encodeURIComponent(token)}`
}

/**
 * @param {(content: object) => void} onRemind
 * @param {(status: PushStatus) => void} [onStatus]
 */
export function createDispatchAuditWs(onRemind, onStatus) {
  let socket = null
  let heartbeatTimer = null
  let reconnectTimer = null
  let reconnectAttempt = 0
  let manualClosed = false

  function setStatus(status) {
    if (typeof onStatus === 'function') {
      onStatus(status)
    }
  }

  function clearHeartbeat() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
  }

  function clearReconnect() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  function scheduleReconnect() {
    if (manualClosed) return
    clearReconnect()
    setStatus('reconnecting')
    const delay = Math.min(RECONNECT_BASE_MS * Math.pow(2, reconnectAttempt), RECONNECT_MAX_MS)
    reconnectAttempt += 1
    reconnectTimer = setTimeout(() => {
      connect()
    }, delay)
  }

  function handleMessage(raw) {
    if (!raw || raw === 'pong') {
      return
    }
    try {
      const json = typeof raw === 'string' ? JSON.parse(raw) : raw
      if (!json || json.type !== MESSAGE_TYPE) {
        return
      }
      let content = json.content
      if (typeof content === 'string') {
        content = JSON.parse(content)
      }
      if (typeof onRemind === 'function') {
        onRemind(content || {})
      }
    } catch (err) {
      console.warn('[dispatchWs] 消息解析失败', err)
    }
  }

  function connect() {
    clearReconnect()
    if (socket) {
      try {
        socket.onclose = null
        socket.close()
      } catch {
        /* ignore */
      }
      socket = null
    }

    const url = buildWsUrl()
    if (!url) {
      setStatus('offline')
      return
    }
    manualClosed = false
    setStatus(reconnectAttempt > 0 ? 'reconnecting' : 'connecting')
    console.info('[dispatchWs] connecting', url.replace(/token=[^&]+/, 'token=***'))
    try {
      socket = new WebSocket(url)
    } catch (err) {
      console.warn('[dispatchWs] 连接失败', err)
      setStatus('offline')
      scheduleReconnect()
      return
    }

    socket.onopen = () => {
      reconnectAttempt = 0
      setStatus('online')
      clearHeartbeat()
      heartbeatTimer = setInterval(() => {
        if (socket && socket.readyState === WebSocket.OPEN) {
          try {
            socket.send('ping')
          } catch {
            /* ignore */
          }
        }
      }, HEARTBEAT_INTERVAL_MS)
    }

    socket.onmessage = (event) => {
      handleMessage(event.data)
    }

    socket.onerror = (err) => {
      console.warn('[dispatchWs] error', err)
    }

    socket.onclose = (event) => {
      console.warn('[dispatchWs] closed', event.code, event.reason || '')
      clearHeartbeat()
      socket = null
      if (!manualClosed) {
        scheduleReconnect()
      } else {
        setStatus('offline')
      }
    }
  }

  function close() {
    manualClosed = true
    clearHeartbeat()
    clearReconnect()
    if (socket) {
      try {
        socket.close()
      } catch {
        /* ignore */
      }
      socket = null
    }
    setStatus('offline')
  }

  return { connect, close }
}
